import "./ActionBar.scss";

/**
 * Plan Manager action bar with optional status alerts and one or more buttons.
 * Pure presentational — emits intent through callbacks; owns no state.
 *
 * @param {object} props
 * @param {"spread" | "end"} [props.layout="end"] - Button row alignment.
 * @param {Array<{
 *   tone: "success" | "error",
 *   message: string,
 *   role?: "status" | "alert",
 * }>} [props.alerts] - Optional inline alerts before the buttons.
 * @param {Array<{
 *   label: string,
 *   variant?: "primary" | "secondary" | "accent" | "outline-accent",
 *   disabled?: boolean,
 *   onClick: () => void,
 * }>} props.actions - Buttons rendered in order.
 * @param {string} [props.className] - Extra class on the root element.
 * @returns {import('react').JSX.Element}
 */
const ActionBar = ({
    layout = "end",
    alerts = [],
    actions,
    className,
}) => {
    const rootClassName = [
        "plan-manager-action-bar",
        `plan-manager-action-bar--layout-${layout}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rootClassName}>
            {alerts.map((alert) => (
                <p
                    key={`${alert.tone}-${alert.message}`}
                    className={`plan-manager-action-bar__alert plan-manager-action-bar__alert--${alert.tone}`}
                    role={
                        alert.role ??
                        (alert.tone === "error" ? "alert" : "status")
                    }
                >
                    {alert.message}
                </p>
            ))}
            <div className="plan-manager-action-bar__actions">
                {actions.map((action) => {
                    const variant = action.variant ?? "primary";

                    return (
                        <button
                            key={action.label}
                            type="button"
                            className={`plan-manager-action-bar__button plan-manager-action-bar__button--${variant}`}
                            disabled={action.disabled}
                            onClick={action.onClick}
                        >
                            {action.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ActionBar;
